/**
 * One edge segment (door, window or opening) drawn on its host line. Paint
 * order: the wipe of the inner outline across the opening, the type glyph,
 * the slide hit strip, then (when selected) the two resize edges and the
 * delete icon. The hit strip must stay above the glyph. The jambs across the wall thickness belong to the wall band (they
 * follow the same touching-edge rule as the 3D), and the glyphs sit within
 * that thickness: windows and pocket doors centered, swing doors on the face
 * they open toward. `wfn(s, o)`'s `o` is measured OUTWARD from the inner line.
 */

import { type FloorPlanEdgeSegment, type Point, WALL_THICKNESS } from '@kaiizen/kai-floor-plan';
import { alongCursor, segmentHandleReach, segmentTrashOffset, svgPoints } from '@kaiizen/kai-floor-plan-editor-core';
import { canvasBg, kaiBlue, roomStroke } from '../../styles';
import { DoorGlyph, type Sp, type Wfn } from './DoorGlyphs';
import { DragEdge, TrashIcon } from './handles';
import { ALCOVE_BODY_STYLE } from './planCss';
import { usePlanExport } from './planExportContext';

/** Half the window glass band's width across the wall (ft). */
const GLASS_HALF_WIDTH = 0.15;

/**
 * A window: a blue glass band centered in the wall's thickness, the same blue
 * as the wall-view glass, so the two views read as the same window. The band
 * has no outline: the end bars span exactly its corners, and an outline
 * (which straddles the edge) would make it read wider than the bars. A thin
 * center line is the standard architectural glass mark, and a thick frame bar
 * caps each end at the band width.
 */
function WindowGlyph({ wfn, seg, sp }: { wfn: Wfn; seg: FloorPlanEdgeSegment; sp: Sp }): React.JSX.Element {
  const p = seg.position, w = seg.width;
  const H = GLASS_HALF_WIDTH, mid = WALL_THICKNESS / 2;
  const band = svgPoints([wfn(p, mid - H), wfn(p, mid + H), wfn(p + w, mid + H), wfn(p + w, mid - H)].map(sp));
  const c0 = sp(wfn(p, mid)), c1 = sp(wfn(p + w, mid));
  return (
    <>
      <polygon points={band} fill={kaiBlue} />
      <line x1={c0.x} y1={c0.y} x2={c1.x} y2={c1.y} stroke={roomStroke} strokeWidth={1} />
      {[p, p + w].map((s0, i) => {
        const a = sp(wfn(s0, mid - H)), b = sp(wfn(s0, mid + H));
        return <line key={i} x1={a.x} y1={a.y} x2={b.x} y2={b.y} stroke={roomStroke} strokeWidth={15} strokeLinecap='butt' />;
      })}
    </>
  );
}

export interface SegmentGlyphProps {
  wfn: Wfn;
  perp: Point;
  seg: FloorPlanEdgeSegment;
  bodyHandle: string;
  startHandle: string;
  endHandle: string;
  delHandle: string;
  active: boolean;
  sp: Sp;
  scale: number;
}

/**
 * The segment slides along its parent line, so its grab cursor is the plain
 * double arrow parallel to the line. It is the one case that keeps the
 * bar-less arrows; everything that resizes a line uses the bar variant
 * (`perpCursor`).
 */
export function SegmentGlyph({
  wfn,
  perp,
  seg,
  bodyHandle,
  startHandle,
  endHandle,
  delHandle,
  active,
  sp,
  scale,
}: SegmentGlyphProps): React.JSX.Element {
  const exporting = usePlanExport();
  const slideCursor = alongCursor(perp);
  const p = seg.position, w = seg.width;
  const A = sp(wfn(p, 0)), B = sp(wfn(p + w, 0));
  const { inward, outward } = segmentHandleReach(scale);
  const strip = svgPoints([wfn(p, outward), wfn(p, -inward), wfn(p + w, -inward), wfn(p + w, outward)].map(sp));
  const c = sp(wfn(p + w / 2, 0));
  const trashOffset = segmentTrashOffset();
  return (
    <g data-layer={seg.edgeSegmentType}>
      <line x1={A.x} y1={A.y} x2={B.x} y2={B.y} stroke={canvasBg} strokeWidth={5} />
      {seg.edgeSegmentType === 'door' && <DoorGlyph wfn={wfn} seg={seg} sp={sp} />}
      {seg.edgeSegmentType === 'window' && <WindowGlyph wfn={wfn} seg={seg} sp={sp} />}
      {!exporting && <polygon css={ALCOVE_BODY_STYLE} data-handle={bodyHandle} points={strip} style={{ cursor: slideCursor }} />}
      {active && (
        <>
          <DragEdge a={sp(wfn(p, outward))} b={sp(wfn(p, -inward))} handle={startHandle} cursor={slideCursor} />
          <DragEdge a={sp(wfn(p + w, outward))} b={sp(wfn(p + w, -inward))} handle={endHandle} cursor={slideCursor} />
          <TrashIcon p={{ x: c.x - perp.x * trashOffset, y: c.y - perp.y * trashOffset }} handle={delHandle} />
        </>
      )}
    </g>
  );
}
